import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook to handle subscription-based feature access
 * Use this in components that need to check subscription status
 */
export const useSubscription = () => {
  const { user, isSubscribed, isSubscriptionExpired, updateSubscriptionStatus } = useAuth();

  // Check if user can access premium features (leagues, registration, etc.)
  const canAccessPremiumFeatures = isSubscribed && !isSubscriptionExpired;

  // Get subscription status message for UI
  const getSubscriptionMessage = () => {
    if (!user) return '';
    if (user.type === 'admin') return ''; // Admins don't need subscriptions
    if (!isSubscribed) return 'Subscribe to register for leagues';
    if (isSubscriptionExpired) return 'Your subscription has expired. Please renew to continue.';
    return '';
  };

  // Get CSS classes for disabled/enabled league cards
  const getLeagueCardClasses = (baseClasses: string = '') => {
    if (!user || user.type === 'admin') return baseClasses;
    
    if (!canAccessPremiumFeatures) {
      return `${baseClasses} opacity-50 cursor-not-allowed grayscale`;
    }
    return `${baseClasses} cursor-pointer hover:shadow-lg transition-all duration-200`;
  };

  // Check if league registration button should be disabled
  const isLeagueRegistrationDisabled = () => {
    if (!user) return true;
    if (user.type === 'admin') return false; // Admins can manage leagues
    return !canAccessPremiumFeatures;
  };

  // Get button text based on subscription status
  const getRegistrationButtonText = () => {
    if (!user) return 'Login Required';
    if (user.type === 'admin') return 'Manage League';
    if (!isSubscribed) return 'Subscribe to Register';
    if (isSubscriptionExpired) return 'Renew Subscription';
    return 'Register for League';
  };

  return {
    user,
    isSubscribed,
    isSubscriptionExpired,
    canAccessPremiumFeatures,
    getSubscriptionMessage,
    getLeagueCardClasses,
    isLeagueRegistrationDisabled,
    getRegistrationButtonText,
    updateSubscriptionStatus
  };
};

/**
 * Helper function to format subscription expiration date
 */
export const formatExpirationDate = (dateString?: string): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Helper function to check if subscription expires soon (within 7 days)
 */
export const isExpiringSoon = (dateString?: string): boolean => {
  if (!dateString) return false;
  
  const expirationDate = new Date(dateString);
  const sevenDaysFromNow = new Date();
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
  
  return expirationDate <= sevenDaysFromNow && expirationDate > new Date();
};
